import {
  cloneElement,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useTheme } from '../context/ThemeContext';

// Cortex Tooltip — an accessible hover/focus tooltip. The trigger is wired to the tooltip
// via aria-describedby; the tooltip shows on mouseenter/focus (after a short delay) and
// hides on mouseleave/blur/Escape. Positioned absolutely relative to the trigger wrapper.

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
  side?: TooltipSide | undefined;
  /** Show delay in ms. */
  delay?: number | undefined;
}

const SIDE_CLASSES: Record<TooltipSide, string> = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
};

export function Tooltip({
  content,
  children,
  side = 'top',
  delay = 200,
}: TooltipProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const id = useId();
  const [visible, setVisible] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const show = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setVisible(true), delay);
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    setVisible(false);
  };

  // Clone the trigger to attach handlers + aria-describedby without an extra wrapper role.
  const trigger = cloneElement(
    children,
    {
      'aria-describedby': id,
      onMouseEnter: show,
      onMouseLeave: hide,
      onFocus: show,
      onBlur: hide,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') hide();
      },
    } as Partial<React.HTMLAttributes<HTMLElement>>,
  );

  return (
    <span className="relative inline-flex">
      {trigger}
      <span
        role="tooltip"
        id={id}
        hidden={!visible}
        className={`pointer-events-none absolute z-[99999] whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-medium shadow-lg transition-opacity duration-150 ${
          SIDE_CLASSES[side]
        } ${visible ? 'opacity-100' : 'opacity-0'} ${
          isDark
            ? 'border border-white/[0.1] bg-[#1a1a24] text-white/90'
            : 'border border-slate-200 bg-white text-slate-700'
        }`}
      >
        {content}
      </span>
    </span>
  );
}
